import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../../hooks/useHiddenColumns';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { ColorType } from '../../../../types/global';
import {
  PersonRole,
  use_ContractsQuery,
  useAdditionalTeachingLoadsListQuery,
} from '../../../../types/planung-graphql-client-defs';
import { sumUpArray } from '../../../../utils/arrayFunc';
import { hexToColorOption } from '../../../../utils/colorUtils';
import { LicenseLock } from '../../../LicenseHint/LicenseHint';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';

type LessonTeacherTableType = {
  uuid: string;
  teacher: string;
  planned: number | null;
  contract: number | null;
  subjects: string[];
  color: ColorType;
  /** Ohne gebuchtes Modul Deputate: Lehrkraft jenseits der ersten fünf, ohne Deputatsdaten (Buchungshinweis). */
  locked?: boolean;
};

type LessonTeacherTableProps = PropsWithChildren;
export const TeachersTable: FC<LessonTeacherTableProps> = observer(() => {
  const { pimAuthClaims } = useAuthClaims();
  const { t } = useTranslation();
  const deputateLicensed = usePermissionChecker()?.isDeputateEnabled() ?? true;
  const navigate = useNavigate();
  const schoolYear = useSelectedSchoolYear();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('lesson-teacher-table');
  const { sorting, saveSorting } = useColumnsSort('lesson-teacher-table');

  const [{ data: contractsData }] = use_ContractsQuery({});

  const { teacherData, subjectData } = useLoadBasicData({ pause: false });

  const { lessons: lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const personData = teacherData?.people.filter((p) => p.role === PersonRole.Teacher && p.active);

  const [{ data: additionalTeachingLoads }] = useAdditionalTeachingLoadsListQuery({
    variables: { schoolyear: schoolYear?.uuid ?? '' },
    context: useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD'),
    pause: !schoolYear,
  });

  const memoizedData: LessonTeacherTableType[] = useMemo(() => {
    return (
      personData?.map((person) => {
        const currentContract = contractsData?.contracts
          .filter((c) => c.personUuid === person.uuid)
          .find(
            (c) =>
              dayjs(c.validFrom).isSameOrBefore(dayjs().startOf('day')) &&
              (dayjs(c.validUntil).isSameOrAfter(dayjs().startOf('day')) || c.validUntil === null),
          );
        const { html, label } = hexToColorOption(person.timetableConfig?.color ?? '');
        return {
          uuid: person.uuid,
          teacher: person.listName ?? '',
          color: { color: html, colorLabel: label },
          planned:
            (lessonsData
              .map((lesson) => lesson.teachingLoadInfo?.deputatByTeacher.get(person?.uuid ?? '') ?? 0)
              .reduce((sum, dep) => sum + dep, 0) ?? 0) +
            sumUpArray(
              additionalTeachingLoads?.additionalTeachingLoads
                ?.filter((a) => a.teacher.uuid === person?.uuid)
                .map((a) => a.hours) ?? [],
            ),
          subjects: person.qualifications
            .map((q) => {
              const subject = subjectData?.subjects.find((s) => s.uuid === q.subjectUuid);
              return subject?.name ?? '';
            })
            .sort(), // TODO remove if the teaching qualifications have a sorting
          contract: currentContract?.hoursWeekly ?? null,
          locked:
            !deputateLicensed &&
            !lessonsData.some((lesson) => lesson.teachingLoadInfo?.deputatByTeacher.has(person.uuid)),
        };
      }) ?? []
    );
  }, [
    additionalTeachingLoads?.additionalTeachingLoads,
    contractsData?.contracts,
    deputateLicensed,
    lessonsData,
    personData,
    subjectData?.subjects,
  ]);

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const tableColumns = useMemo((): TableColumns<LessonTeacherTableType>[] => {
    return [
      {
        header: t('persons.title', { count: 1 }),
        id: 'teacher',
        accessorKey: 'teacher',
        meta: {
          filterName: t('persons.title', { count: 1 }),
        },
        size: 300,
      },
      {
        header: t('deputate.planned'),
        id: 'planned',
        accessorKey: 'planned',
        meta: {
          filterName: t('deputate.planned'),
        },
        accessorFn: (row) => {
          return row.planned?.toFixed(2);
        },
        cell: ({ row }) =>
          row.original.locked ? <LicenseLock hint={{ kind: 'teachingLoad' }} /> : row.original.planned?.toFixed(2),
        alignment: 'right',
        size: 50,
      },
      {
        header: t('contracts.contract'),
        id: 'contract',
        accessorKey: 'contract',
        meta: {
          filterName: t('contracts.contract'),
        },
        accessorFn: (row) => {
          return row.contract?.toFixed(2) ?? '--';
        },
        alignment: 'right',
        size: 50,
      },
      {
        header: t('subject.title', { count: 2 }),
        id: 'subjects',
        accessorKey: 'subjects',
        accessorFn: (row) => {
          return row.subjects.join(', ');
        },
        cell: ({ row }) => {
          return <>{row.original.subjects.join(', ')}</>;
        },
        size: 250,
      },
      {
        header: t('common.color'),
        accessorKey: 'color',
        id: 'color',
        type: 'color',
      },
      {
        header: t('common.status'),
        accessorFn: (row) => {
          if (row.planned === null || row.contract === null) {
            return '---';
          }
          const status = Math.floor((row.planned / row.contract) * 100);
          return status + '%';
        },
        cell: ({ row }) => {
          if (
            row.original.planned === null ||
            row.original.planned === 0 ||
            row.original.contract === null ||
            row.original.contract === 0
          ) {
            return <></>;
          }
          const status = Math.floor((row.original.planned / row.original.contract) * 100);
          return (
            <span
              style={{
                color:
                  status > 100 ? 'var(--color-error)' : status < 100 ? 'var(--color-warning)' : 'var(--color-success)',
              }}
            >
              {status}%
            </span>
          );
        },
        size: 75,
        id: 'status',
        accessorKey: 'status',
      },
    ];
  }, []);

  return (
    <Table<LessonTeacherTableType>
      showPagination={true}
      pageSize={50}
      showBorderRadius
      showShadow
      showSort
      canScroll
      minHeight={1000}
      isOnWhite={false}
      showVisibility
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={tableColumns}
      data={memoizedData}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('persons.title', { count: 2 })} (${t('lesson.title', { count: 2 })}) - ${t(
          'common.schoolYear',
        )} ${schoolYear?.shortName}`,
        filename: `${t('persons.title', { count: 2 })}_${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
      }}
      showActionBar
      actionBarSettings={{
        showExpertFilter: true,
        showPrintButton: true,
      }}
      showRowHover
      onRowClick={(event, row) => {
        navigate(row.original.uuid);
      }}
      lastCol={(row) => {
        return <Button hierarchy='ghost' icon={<ArrowRightIcon className='small' />} />;
      }}
    />
  );
});
